// LBD-CUSTOM: Sport selector (Option A).
//
// Drives the hidden stock <variant-picker> inputs of the option grouped by the
// product metafield custom.logo_groups. The placement buttons and the sport
// <select> are rendered by snippets/lbd-sport-selector.liquid as a sibling of
// <variant-picker>, never inside it, so the picker's own
// `select option[selected], fieldset input:checked` queries never see them.
//
// This element holds no state of its own. Everything it shows is re-read from
// the picker's server-rendered inputs on connect and after every
// `variant:update`, which Horizon dispatches after it has morphed the picker.
// A Size or Colour change therefore can never desync it, and deep links work
// because Shopify checks the right input server-side.
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} LbdSportSelectorRefs
 * @property {HTMLSelectElement} [sportSelect]
 * @property {HTMLElement} [sportPill]
 */

/**
 * @extends {Component<LbdSportSelectorRefs>}
 */
class LbdSportSelector extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#sync, { signal });

    this.#sortSportOptions();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // Fired by the morph when a whole section is re-rendered (combined listings, featured product).
  updatedCallback() {
    super.updatedCallback();
    this.#sortSportOptions();
    this.#sync();
  }

  /**
   * Order the sport options by PromoTastic's `order` first, then by name in the page's
   * locale. Liquid has no locale-aware sort, and every `order` is 0 until PIM sets
   * display_order, so today this is a French-correct alphabetical list (Vélotechno before
   * Volleyball). The day PIM sets real values, they take over without a theme change.
   */
  #sortSportOptions() {
    const select = this.refs.sportSelect;
    if (!(select instanceof HTMLSelectElement)) return;

    const lang = document.documentElement.lang || undefined;
    const options = Array.from(select.options).filter((option) => option.value);
    const sorted = [...options].sort((a, b) => {
      const byOrder = Number(a.dataset.order ?? 0) - Number(b.dataset.order ?? 0);
      if (byOrder !== 0) return byOrder;
      return a.text.localeCompare(b.text, lang, { sensitivity: 'base' });
    });

    if (sorted.every((option, index) => option === options[index])) return;

    const selectedValue = select.value;
    for (const option of sorted) select.append(option);
    select.value = selectedValue;
  }

  /** Same scope product-form-component listens on, so quick-add dialogs and cards behave alike. */
  get #scope() {
    return this.closest('.shopify-section, dialog, product-card') ?? document;
  }

  /** @returns {Element | null} */
  get #picker() {
    return this.#scope.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
  }

  /**
   * The stock input for an option value: a radio in buttons mode, an <option> in dropdowns mode.
   * @param {string} optionValueId
   * @returns {Element | null}
   */
  #input(optionValueId) {
    if (!optionValueId) return null;
    return this.#picker?.querySelector(`[data-option-value-id="${optionValueId}"]`) ?? null;
  }

  /** @param {Element | null} input */
  #isSelected(input) {
    if (input instanceof HTMLInputElement) return input.checked;
    if (input instanceof HTMLOptionElement) return input.selected;
    return false;
  }

  /**
   * Select an option value by driving the stock input and letting <variant-picker>
   * do everything else: cache update, fetch, morph, URL, events.
   * @param {string} optionValueId
   */
  #select(optionValueId) {
    const input = this.#input(optionValueId);
    if (!input || this.#isSelected(input)) return;

    if (input instanceof HTMLInputElement) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (input instanceof HTMLOptionElement) {
      const select = input.closest('select');
      if (!select) return;
      select.value = input.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-lbd-target]');
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;
    this.#select(button.dataset.lbdTarget ?? '');
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || select !== this.refs.sportSelect) return;
    // Guard against ever being nested inside <variant-picker>, whose change handler
    // would otherwise treat this select as a real option.
    event.stopPropagation();
    if (select.value) this.#select(select.value);
  };

  /** Mirror the picker's current selection and availability onto the controls. */
  #sync = () => {
    if (!this.#picker) return;

    for (const button of this.querySelectorAll('[data-lbd-target]')) {
      if (!(button instanceof HTMLElement)) continue;
      const input = this.#input(button.dataset.lbdTarget ?? '');
      button.setAttribute('aria-pressed', String(this.#isSelected(input)));
      if (input instanceof HTMLElement) {
        button.toggleAttribute('data-unavailable', input.dataset.optionAvailable === 'false');
      }
    }

    const select = this.refs.sportSelect;
    if (!(select instanceof HTMLSelectElement)) return;

    let selectedSport = '';
    for (const option of select.options) {
      if (option.value && this.#isSelected(this.#input(option.value))) {
        selectedSport = option.value;
        break;
      }
    }

    // An empty value lands on the disabled placeholder: a sport is only ever shown when one is chosen.
    select.value = selectedSport;
    this.refs.sportPill?.classList.toggle('is-selected', selectedSport !== '');
  };
}

if (!customElements.get('lbd-sport-selector')) {
  customElements.define('lbd-sport-selector', LbdSportSelector);
}
